import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  computed,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatCardAppearance, MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { environment } from '@siat-fe/env';
import {
  Observable,
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

/*
 * A raw tipologia item as returned by the backend; shape is data-driven via
 * `ItemsListConfig.idKey`/`visibleKeys`, so it is not a fixed DTO.
 */
type TipologyItem = Record<string, unknown>;

/*
 * Interface defining a single property key-value pair for UI display
 */
interface UiProperty {
  key: string;
  value: unknown;
}

/*
 * Interface defining the structure of an item used in the view
 */
interface UiItem {
  id: number | string;
  originalData: TipologyItem;
  properties: UiProperty[];
  selected: boolean;
}

export interface ItemsListConfig {
  /** Il nome della proprietà da usare come ID visualizzato nel box colorato (default: 'id') */
  idKey?: string;
  /** Un array di chiavi da visualizzare nel corpo dell'item. Se non specificato, mostra tutto tranne l'ID. */
  visibleKeys?: string[];
}

@Component({
  selector: 'siat-items-list',
  imports: [
    CommonModule,
    ScrollingModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatIcon,
    MatIconButton,
    MatTooltip,
    TitleCasePipe,
  ],
  templateUrl: './items-list.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ItemsListComponent),
      multi: true,
    },
  ],
})
export class ItemsListComponent implements ControlValueAccessor {
  /*
   * Dependency injection for HTTP Client
   */
  private http = inject(HttpClient);

  /*
   * Typologies migrated to v2.
   */
  private readonly V2_TIPOLOGY_ENDPOINTS = new Set([
    'luoghi_erogazione_prestazione',
    'tipo_evento_calendario',
  ]);

  /*
   * Delay in milliseconds before executing the search
   */
  private readonly DEBOUNCE_MS = 300;

  /*
   * Item height in pixel.
   */
  readonly ITEM_SIZE = 80;

  /*
   * Maximum viewport height in pixel (default fallback).
   */
  readonly MAX_VIEWPORT_HEIGHT = 400;

  /*
   * Minimum viewport height in pixel.
   */
  readonly MIN_VIEWPORT_HEIGHT = 200;

  /*
   * Required input determining the API entity to query
   */
  queryParamName = input.required<string>();

  /*
   * Required input determining the label to display
   */
  label = input.required<string>();

  /**
   * value of detail element
   */

  valueItem = input<{ id: number; codice: string; descrizione: string } | null>(
    null
  );

  /*
   * Required input determining the placeholder to display
   */
  placeholder = input.required<string>();

  /**
   * The configuration for the items list
   */
  config = input<ItemsListConfig>();

  /*
   * Optional input to set the initial visible height based on number of items.
   * Example: if 5, the list height will be exactly enough to show 5 items.
   * User can scroll to see the rest.
   */
  maxItems = input<number>();

  /**
   * Mat card appareance.
   */
  matCardAppearance = input<MatCardAppearance>('outlined');

  /*
   * Minimum characters required to trigger a specific search
   */
  minSearchChars = input<number>(2);

  /*
   * Whether an empty search term loads the whole list
   */
  loadAllOnEmpty = input<boolean>(true);

  /*
   * Optional hint shown under the "no results" message
   */
  emptyHint = input<string>();

  /*
   * Stream of selected endpoint.
   */
  private endpoint$ = toObservable(this.queryParamName);

  /*
   * Form group handling the search input field
   */
  searchForm = new FormGroup({
    searchControl: new FormControl<string>(''),
  });

  /*
   * Signal indicating if an API request is in progress
   */
  isLoading = signal(false);

  /*
   * Internal signal holding the currently selected item data
   */
  private selectedItem = signal<TipologyItem | null>(null);

  /*
   * Callback function for CVA change propagation
   */
  private onChange: (value: TipologyItem | null) => void = () => {};

  /*
   * Callback function for CVA touched propagation
   */
  private onTouched: () => void = () => {};

  /*
   * Stream of search terms handling debounce and distinct changes
   */
  private results$: Observable<TipologyItem[]> = this.endpoint$.pipe(
    tap(() => {
      this.selectedItem.set(this.valueItem() ?? null);
      this.onChange(this.valueItem() ?? null);
      this.searchControl.setValue('', { emitEvent: false });
    }),
    switchMap(endpoint => {
      return this.searchControl.valueChanges.pipe(
        startWith(this.searchControl.value || ''),
        debounceTime(this.DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap(term => this.fetchData(term, endpoint))
      );
    })
  );

  /*
   * Signal per l'item selezionato (UI)
   */
  selectedUiItem = computed<UiItem | null>(() => {
    const selected = this.selectedItem();
    if (!selected) return null;

    return this.mapToUiItem(selected, selected);
  });

  /*
   * Clear the selected item.
   */
  clearSelection(): void {
    this.selectedItem.set(null);
    this.onChange(null);
    this.searchControl.setValue('');
  }
  /*
   * Signal derived from the results stream, holding the raw server list
   */
  private serverItems = toSignal(this.results$, { initialValue: [] });

  /*
   * Computed signal transforming raw server items into UI-ready items.
   * NOTE: We do NOT slice the array here anymore, so scroll works for all items.
   */
  displayedItems = computed<UiItem[]>(() => {
    const items = this.serverItems();
    const currentSelection = this.selectedItem();

    if (!items || !Array.isArray(items)) return [];

    return items.map(item => this.mapToUiItem(item, currentSelection));
  });

  /*
   * Computed signal to get the total number of items from the server
   */
  totalServerItemsCount = computed(() => this.serverItems()?.length || 0);

  /*
   * Helper method to map raw data to UiItem structure
   */
  private mapToUiItem(
    item: TipologyItem,
    currentlySelectedItem: TipologyItem | null
  ): UiItem {
    const conf = this.config();
    const idKey = conf?.idKey || 'id';
    const displayId = item[idKey] as number | string;
    let properties: UiProperty[] = [];

    if (conf?.visibleKeys && conf.visibleKeys.length > 0) {
      properties = conf.visibleKeys.map(key => ({
        key: key,
        value: item[key],
      }));
    } else {
      properties = Object.keys(item)
        .filter(key => key !== idKey && key !== 'selected')
        .map(key => ({ key, value: item[key] }));
    }
    const isSelected = currentlySelectedItem
      ? currentlySelectedItem[idKey] === item[idKey]
      : false;

    return {
      id: displayId,
      originalData: item,
      properties,
      selected: isSelected,
    };
  }

  /*
   * Handles the logic for choosing the correct API endpoint based on input length
   */
  private fetchData(
    term: string | null,
    endpoint: string
  ): Observable<TipologyItem[]> {
    const cleanTerm = term?.trim() || '';

    if (cleanTerm.length === 0 && this.loadAllOnEmpty()) {
      return this.executeRequest(
        `${this.getTipologyBaseUrl(endpoint)}findAll/${endpoint}`
      );
    }

    if (cleanTerm.length < this.minSearchChars()) {
      this.isLoading.set(false);
      return of([]);
    }

    const encodedTerm = encodeURIComponent(cleanTerm);
    return this.executeRequest(
      `${this.getTipologyBaseUrl(endpoint)}find/${endpoint}/${encodedTerm}`
    );
  }

  private getTipologyBaseUrl(endpoint: string): string {
    const apiVersion = this.V2_TIPOLOGY_ENDPOINTS.has(endpoint) ? 'v2' : 'v1';
    return `${environment.baseUrl}${apiVersion}/tipology/`;
  }

  /*
   * Executes the HTTP request and manages the loading state
   */
  private executeRequest(url: string): Observable<TipologyItem[]> {
    this.isLoading.set(true);
    return this.http.get<TipologyItem[]>(url).pipe(
      catchError(err => {
        console.error('API Error:', err);
        return of([]);
      }),
      tap(() => this.isLoading.set(false))
    );
  }

  /*
   * Handles user click on an item, toggling selection state
   */
  toggleSelection(uiItem: UiItem): void {
    const current = this.selectedItem();
    const isSameItem = current && current['id'] === uiItem.id;

    const newValue = isSameItem ? null : uiItem.originalData;

    this.selectedItem.set(newValue);
    this.onChange(newValue);
    this.onTouched();
  }

  /*
   * Helper getter to access the search form control
   */
  get searchControl(): FormControl {
    return this.searchForm.get('searchControl') as FormControl;
  }

  /*
   * Writes a new value to the element (model -> view)
   */
  writeValue(obj: TipologyItem | null): void {
    this.selectedItem.set(obj);
  }

  /**
   * Registers a function to call when the field value changes in the UI.
   * This is needed to communicate the update to angular forms API.
   */
  registerOnChange(
    changeCallback: (value: TipologyItem | null) => void
  ): void {
    this.onChange = changeCallback;
  }

  /**
   * Allows to register a function to be called when the field is touched
   * (it's a "noop" method but it's needed to correctly implement ControlValueAccessor interface).
   */
  registerOnTouched(touchedCallback: () => void) {
    this.onTouched = touchedCallback;
  }

  /*
   * Called when the control's status changes to or from 'DISABLED'
   */
  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  /*
   * Signal holding the viewport height.
   * Logic:
   * 1. Calculate height of ALL items.
   * 2. If maxItems is set, calculate height of MAX items.
   * 3. Take the smaller of the two (so we don't have empty space if items < max).
   * 4. If maxItems is NOT set, default to MAX_VIEWPORT_HEIGHT.
   */
  viewportHeight = computed(() => {
    const itemCount = this.displayedItems().length;

    if (this.isLoading() || itemCount === 0) {
      return this.MIN_VIEWPORT_HEIGHT;
    }

    const contentHeight = itemCount * this.ITEM_SIZE;
    const maxLimit = this.maxItems();

    if (maxLimit && maxLimit > 0) {
      // Return the height for 'maxLimit' items, but clamp it to contentHeight
      // if there are fewer items than the limit.
      return Math.min(contentHeight, maxLimit * this.ITEM_SIZE);
    }

    return Math.min(contentHeight, this.MAX_VIEWPORT_HEIGHT);
  });
}
